'use client';
import useNavigationContext from '@susu/custom-made/providers/hooks/useNavigationContext';
import useOptionContext from '@susu/custom-made/providers/hooks/useOptionContext';
import useUserContext from '@susu/custom-made/providers/hooks/useUserContext';
import { Clients } from '@susu/custom-made/utils/constants';
import { Section } from '@susu/custom-made/utils/navigation';
import { shouldAddAdditonalProduct } from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import type { ReactNode } from 'react';

// Panels arrive as elements, so option-state re-renders here don't re-render them.
export const SectionLayout = ({
  currentSection,
  preview,
  panel,
  summary,
  modals,
}: Readonly<{
  currentSection: string;
  preview: ReactNode;
  panel: ReactNode;
  summary: ReactNode;
  modals: ReactNode;
}>) => {
  const { section } = useNavigationContext();
  const { client, product } = useUserContext();
  const { isBaseStyleSelection, isAdditionalProductAdded } = useOptionContext();
  const showPreview =
    currentSection === Section.Fabric ||
    ((!isBaseStyleSelection || shouldAddAdditonalProduct(isAdditionalProductAdded, product!, section)) &&
      currentSection !== Section.Summary);

  // `leading-[normal]`: hosts that ship Tailwind's preflight set `html { line-height: 1.5 }`, which pushes icon glyphs
  // (sized to their box) off-centre. Reset it here once for every icon below instead of per icon.
  return (
    <div
      className={cn(
        "h-full w-full leading-[normal] [grid-template-areas:'preview'_'section'] grid-cols-[1fr] grid-rows-[1fr_auto] lg:flex",
        !showPreview ? 'flex' : 'grid',
      )}
    >
      {showPreview && (
        <div className="bg-gray-300 [grid-area:preview] lg:w-[60%]">
          <div className="h-full">{preview}</div>
        </div>
      )}
      <div className={cn('[grid-area:section]', !showPreview ? 'w-full' : 'lg:w-[40%]')}>
        {currentSection !== Section.Summary && (
          <section className="h-full">
            <div
              className={cn(
                !showPreview
                  ? 'h-full overflow-y-auto flex'
                  : 'overflow-hidden h-[188px] md:h-[244px] lg:h-full lg:flex',
                client === Clients.inStore && (!showPreview ? '' : 'mb-[34px] md:mb-20 lg:mb-0'),
              )}
            >
              {panel}
            </div>
          </section>
        )}
        {currentSection === Section.Summary && summary}
      </div>
      {modals}
    </div>
  );
};
